import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type CombatRange } from '@/lib/api';
import { cn } from '@/lib/utils';
import { KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { COMBAT_PROFILE_META, formatEfficiency } from '@/lib/combatEfficiency';

/**
 * Player-hub Stats card (Plan 053): own kills per M of strength against the
 * power class median. No other names.
 */
export default function CombatEfficiencyCard({ token }: { token: string }) {
  const [range, setRange] = useState<CombatRange>('90d');
  const q = useQuery({
    queryKey: ['player-combat', token, range],
    queryFn: () => api.getSelfReportCombatEfficiency(token, range),
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const data = q.data;
  const own = data?.own ?? null;
  const meta = own ? KILL_GROWTH_CLASS_META[own.class] : null;
  const profile = own?.profile ? COMBAT_PROFILE_META[own.profile] : null;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Kills per strength
        </h2>
        <div className="inline-flex rounded border border-slate-800 bg-slate-950 p-0.5 text-[11px]">
          {(['30d', '90d'] as const).map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={cn(
                'rounded px-2 py-0.5',
                range === r ? 'bg-slate-700 text-white' : 'text-slate-400',
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        {q.isLoading ? (
          <div className="text-xs text-slate-500">Loading…</div>
        ) : !own || !meta || !data ? (
          <p className="text-xs text-slate-400">
            Not enough data yet — this needs kill reports at both ends of the range and
            a power report around its middle.
          </p>
        ) : (
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-3xl font-semibold tabular-nums" style={{ color: meta.color }}>
                {formatEfficiency(own.efficiency)}
              </div>
              <div className="text-xs tabular-nums text-slate-400">
                {own.killsEstimated ? '≈' : ''}+{own.deltaKillsM.toFixed(2)} M kills with{' '}
                {own.mSum.toFixed(1)} M-Sum
              </div>
              <div className="mt-1 text-xs text-slate-300">
                Class median {formatEfficiency(data.classMedian?.medianEfficiency)} · #
                {own.classRank} of {own.classSize}
              </div>
            </div>
            <div className="text-right">
              <span
                className={cn(
                  'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider',
                  meta.chip,
                )}
              >
                {meta.label}
              </span>
              {profile && (
                <div className="mt-1">
                  <span
                    className={cn('rounded border px-1.5 py-0.5 text-[10px]', profile.chip)}
                    title={profile.hint}
                  >
                    {profile.label}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
